/**
 * Sniper — pure, framework-free helpers for the per-group run state introduced
 * by backend PR #200 (`running` / `mode` / `panicked` on `GET /sniper/groups`).
 * Kept out of the components so the badge derivation + start wiring are
 * unit-testable (mirror of `groupPodStatus.ts` on the opener). There is NO
 * client-side fabrication — every value here is a projection of the backend
 * group state, never a guess.
 */

import type {
  SniperGroup,
  SniperGroupMode,
  StartSniperGroupDto,
} from '../types/sniper.types';

/** MUI severity used for the group state badge / start-confirm styling. */
export type SniperBadgeSeverity = 'error' | 'warning' | 'default';

export interface SniperGroupBadge {
  label: string;
  severity: SniperBadgeSeverity;
  /** True only in the halted (panicked) state — rendered distinctly. */
  panicked: boolean;
}

/**
 * The at-a-glance state badge for a group row/card:
 *   - panicked            → "PANICKED"        (error, distinct)
 *   - running + normal    → "RUNNING · NORMAL" (error — live real money)
 *   - running + test      → "RUNNING · TEST"   (warning/amber)
 *   - running + no mode   → "RUNNING"          (warning)
 *   - stopped             → "stopped"          (default)
 * Panicked takes precedence over running so a halted group never reads as live.
 */
export function deriveSniperGroupBadge(
  group: Pick<SniperGroup, 'running' | 'mode' | 'panicked'>,
): SniperGroupBadge {
  if (group.panicked) {
    return { label: 'PANICKED', severity: 'error', panicked: true };
  }
  if (group.running) {
    const modeLabel =
      group.mode === 'normal' ? 'NORMAL' : group.mode === 'test' ? 'TEST' : null;
    return {
      label: modeLabel ? `RUNNING · ${modeLabel}` : 'RUNNING',
      // Normal = live real money → red; test/unknown → amber.
      severity: group.mode === 'normal' ? 'error' : 'warning',
      panicked: false,
    };
  }
  return { label: 'stopped', severity: 'default', panicked: false };
}

/** The two Start modes, in split-button display order (test first, softer). */
export const SNIPER_START_MODES: SniperGroupMode[] = ['test', 'normal'];

/** Build the `POST /sniper/groups/:id/start` body for a chosen mode. */
export function startSniperGroupPayload(
  mode: SniperGroupMode,
): StartSniperGroupDto {
  return { mode };
}

/**
 * Confirm-dialog styling per start mode: `normal` is a live real-money account
 * (hard, red confirm); `test` is the low-balance test account (softer, amber).
 */
export function sniperStartConfirmSeverity(
  mode: SniperGroupMode,
): 'error' | 'warning' {
  return mode === 'normal' ? 'error' : 'warning';
}

/** Human label for a start mode. */
export function sniperModeLabel(mode: SniperGroupMode): string {
  return mode === 'normal' ? 'Normal (live account)' : 'Test (test account)';
}

/** Total number of persisted Sniper markets across a group's events. */
export function sniperGroupMarketCount(
  group: Pick<SniperGroup, 'events'>,
): number {
  return (group.events ?? []).reduce(
    (sum, e) => sum + (e.markets?.length ?? e.marketCount ?? 0),
    0,
  );
}

/** True when ANY group is currently halted — drives the global halt indicator. */
export function anySniperHalt(
  groups: Array<Pick<SniperGroup, 'panicked'>>,
): boolean {
  return groups.some((g) => g.panicked);
}
